//client/src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  Link,
} from "react-router-dom";

import LandingPage from "./pages/LandingPage";
import Home from "./pages/Home";
import About from "./pages/About";
import Help from "./pages/Help";
import Founder from "./pages/Founder";
import Feedback from "./pages/Feedback";
import Profile from "./pages/Profile";

import ChatRoom from "./pages/ChatRoom";
import Login from "./pages/Login";
import Register from "./pages/Register";
import TaskDetails from "./pages/TaskDetails";
import TaskDashboard from "./pages/TaskDashboard";
import TaskList from "./pages/TaskList";
import PostTask from "./pages/PostTask";

import EditProfile from "./pages/EditProfile";
import EditTask from "./pages/EditTask";

import LostFoundList from "./pages/LostFoundList";
import LostFoundCreate from "./pages/LostFoundCreate";
import LostFoundDetails from "./pages/LostFoundDetails";

import RecruitList from "./pages/RecruitList";
import RecruitCreate from "./pages/RecruitCreate";
import RecruitDetails from "./pages/RecruitDetails";

import NotificationBell from "./components/NotificationBell";
import { AuthProvider, useAuth } from "./context/AuthContext";

/* THEME */
function useTheme() {
  const [theme, setTheme] = React.useState(
    localStorage.getItem("cp_theme") || "light"
  );

  React.useEffect(() => {
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
    localStorage.setItem("cp_theme", theme);
  }, [theme]);

  return { theme, setTheme };
}

/* NAVBAR */
function NavbarInline() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();

  const isMobile = window.innerWidth < 768;

  if (location.pathname === "/") return null;

  return (
    <nav className="sticky top-0 z-50 backdrop-blur bg-white/80 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-700">
      {isMobile ? (
        <div className="px-4 py-3 space-y-3">

          {/* ROW 1 */}
          <div className="flex justify-between items-center">
            {/* LEFT */}
            <div className="flex items-center gap-3">
              <img src="/logo.png" className="w-9 h-9" alt="logo" />
              <span className="text-lg font-semibold text-indigo-700 dark:text-indigo-300">
                CollegePal
              </span>
            </div>

            {/* RIGHT */}
            <div className="flex items-center gap-4 ml-4">
              <NotificationBell />

              <button onClick={() => setTheme(theme === "light" ? "dark" : "light")}>
                {theme === "light" ? (
                  // Moon
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
                      stroke="#1e293b"
                      strokeWidth="1.5"
                    />
                  </svg>
                ) : (
                  // Sun
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 4V2M12 22v-2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
                      stroke="#f8fafc"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </button>

              {user ? (
                <button
                  onClick={logout}
                  className="px-3 py-1 bg-red-500 text-white rounded-md text-sm"
                >
                  Logout
                </button>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="px-3 py-1 bg-green-600 text-white rounded-md text-sm"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* ROW 2 – USERNAME (RIGHT ALIGNED) */}
          {user && (
            <div className="text-centre text-sm text-slate-600 dark:text-slate-300">
              Hey, <span className="font-medium">{user.name || user.email}</span>
            </div>
          )}

          {/* ROW 3 – LINKS */}
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-slate-700 dark:text-slate-300">
            <Link to="/home">Home</Link>
            <Link to="/help">Help</Link>
            <Link to="/about">About</Link>
            <Link to="/founder">Founder</Link>
            <Link to="/feedback">Feedback</Link>
            <Link to="/profile">Profile</Link>
          </div>
        </div>
      ) : (
        /* DESKTOP */
        <div className="px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo.png" className="w-10 h-10" alt="logo" />
            <span className="text-2xl font-bold text-indigo-700 dark:text-indigo-200">
              CollegePal
            </span>
          </div>

          <div className="flex items-center gap-5 dark:text-slate-300">
            <Link to="/home">Home</Link>
            <Link to="/help">Help</Link>
            <Link to="/about">About</Link>
            <Link to="/founder">Founder</Link>
            <Link to="/feedback">Feedback</Link>
            <Link to="/profile">Profile</Link>

            <NotificationBell />

            <button onClick={() => setTheme(theme === "light" ? "dark" : "light")}>
              {theme === "light" ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
                    stroke="#1e293b"
                    strokeWidth="1.5"
                  />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 4V2M12 22v-2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
                    stroke="#f8fafc"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </button>

            {user ? (
              <>
                <span className="text-sm text-slate-500">
                  Hey, {user.name || user.email}
                </span>
                <button
                  onClick={logout}
                  className="px-3 py-1 bg-red-500 text-white rounded"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="px-3 py-1 bg-blue-600 text-white rounded">
                  Login
                </Link>
                <Link to="/register" className="px-3 py-1 bg-green-600 text-white rounded">
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

/* ROUTES */
function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/home" element={<Home />} />
      <Route path="/tasks" element={<TaskList />} />
      <Route path="/task/:taskId" element={<TaskDetails />} />
      <Route path="/task/:id/edit" element={<EditTask />} />
      <Route path="/post-task" element={<PostTask />} />
      <Route path="/dashboard/task/:id" element={<TaskDashboard />} />

      <Route path="/help" element={<Help />} />
      <Route path="/about" element={<About />} />
      <Route path="/founder" element={<Founder />} />
      <Route path="/feedback" element={<Feedback />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/profile/edit" element={<EditProfile />} />

      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      <Route path="/chat/:type/:postId/:participantId" element={<ChatRoom />} />

      <Route path="/lost-found" element={<LostFoundList />} />
      <Route path="/lost-found/create" element={<LostFoundCreate />} />
      <Route path="/lost-found/:id" element={<LostFoundDetails />} />

      <Route path="/recruit" element={<RecruitList />} />
      <Route path="/recruit/create" element={<RecruitCreate />} />
      <Route path="/recruit/:id" element={<RecruitDetails />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppShell() {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-slate-900">
      <NavbarInline />
      <div className="flex-grow">
        <AppRoutes />
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <AppShell />
      </Router>
    </AuthProvider>
  );
}
